// Function in this file take `supabaseClient` as a parameter and can therefore be used client and server side
import { SupabaseClient } from '@supabase/supabase-js';

import { getErrorMessage } from '@common/utils/errorMessage';
import { handleError } from '@common/utils/handleError';
import { EVENT_INDEX_PAGE_SIZE } from '@data/constants';
import { getEventList } from '@data/supabase/getEventList';
import {
	FilterQueryArguments,
	SupabaseInviteRecord,
	SupabaseProductRecordWithPrices,
	SupabaseSubscriptionRecordWithPrices,
} from '@interfaces';

interface GetEventDataParams {
	supabaseClient: SupabaseClient;
	queryArguments: FilterQueryArguments;
	pageNumber: number;
}

export async function getEventData({
	supabaseClient,
	queryArguments,
	pageNumber,
}: GetEventDataParams) {
	// Determine pagination range
	const rangeFrom = (pageNumber - 1) * EVENT_INDEX_PAGE_SIZE;
	const rangeTo = pageNumber * EVENT_INDEX_PAGE_SIZE - 1;

	const { events, count } = await getEventList({
		options: {
			dateFrom: queryArguments.dateFrom,
			dateTo: queryArguments.dateTo,
			excludeDice: true,
			range: {
				from: rangeFrom,
				to: rangeTo,
			},
			type: queryArguments.type,
			venues: queryArguments.venue ? [queryArguments.venue] : undefined,
		},
		supabaseClient,
	});

	let hasNextPage = false;

	if (typeof count === 'number') {
		hasNextPage = count > pageNumber * EVENT_INDEX_PAGE_SIZE;
	}

	return {
		events,
		pageNumber,
		hasNextPage,
	};
}

interface GetInviteRecordParams {
	supabaseClient: SupabaseClient;
	inviteId: string;
	isRedeemed: boolean;
}

export const getInviteRecord = async ({
	supabaseClient,
	inviteId,
	isRedeemed,
}: GetInviteRecordParams) => {
	try {
		const { error, data } = await supabaseClient
			.from('invites')
			.select<'*', SupabaseInviteRecord>('*')
			.eq('id', inviteId)
			.eq('is_redeemed', isRedeemed)
			.maybeSingle();

		if (error) {
			throw error;
		}

		return data;
	} catch (error) {
		handleError(getErrorMessage(error));

		return null;
	}
};

interface GetSubscriptionParams {
	supabaseClient: SupabaseClient;
}

export const getSubscription = async ({
	supabaseClient,
}: GetSubscriptionParams) => {
	try {
		const { error, data } = await supabaseClient
			.from('subscriptions')
			.select<
				'*, prices(*, products(*))',
				SupabaseSubscriptionRecordWithPrices
			>('*, prices(*, products(*))')
			.in('status', ['trialing', 'active', 'past_due'])
			.order('created', { ascending: true })
			.limit(1);

		if (error) {
			handleError(error.message);
		}

		if (data?.length) {
			return data[0];
		}

		return null;
	} catch (error) {
		handleError(getErrorMessage(error));

		return null;
	}
};

interface GetProductForTierLevelParams {
	supabaseClient: SupabaseClient;
	tierLevel: '1' | '2' | '3';
}

export const getProductForTierLevel = async ({
	supabaseClient,
	tierLevel,
}: GetProductForTierLevelParams) => {
	const { data, error } = await supabaseClient
		.from('products')
		.select<'*, prices(*)', SupabaseProductRecordWithPrices>('*, prices(*)')
		.eq('active', true)
		.eq('prices.active', true)
		.eq('metadata->>tier_level', tierLevel)
		.maybeSingle();

	if (error) {
		handleError(error.message);
	}

	if (data) {
		return data;
	}

	return null;
};
